import { motion, useReducedMotion } from "framer-motion";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { FontScaleControl } from "../components/FontScaleControl";
import { ThemeControl } from "../components/ThemeControl";
import { WorthyHumanBadge } from "../components/WorthyHumanBadge";
import { useAccount } from "../context/AccountContext";
import { CUSTOMER_CONTACT_EMAIL, CUSTOMER_CONTACT_MAILTO } from "../data/contact";

const navClass = ({ isActive }: { isActive: boolean }) =>
	[
		"inline-flex min-h-11 items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150 ease-in-out",
		isActive ? "bg-wi-text/[0.09] text-wi-text" : "text-wi-text/72 hover:bg-wi-text/[0.05] hover:text-wi-text",
	].join(" ");

export function RootLayout() {
	const location = useLocation();
	const { tier } = useAccount();
	const showBadge = tier === "supporter" || tier === "team";
	const prefersReducedMotion = useReducedMotion() === true;
	return (
		<div className="flex min-h-screen flex-col">
			<a
				href="#main-content"
				className="sr-only rounded-lg bg-wi-gold/15 px-3 py-2 font-medium text-wi-text text-sm underline underline-offset-2 focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:inline-block focus:outline-none focus:ring-2 focus:ring-wi-gold/45"
			>
				Skip to main content
			</a>
			<header className="sticky top-0 z-10 border-wi-text/[0.07] border-b bg-wi-surface/90 shadow-wi-nav backdrop-blur-md">
				<div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-4 py-3.5 md:px-6 2xl:px-24">
					<div className="flex items-center gap-2.5">
						<NavLink to="/" className="group flex flex-col">
							<span className="font-display text-lg text-wi-text tracking-tight transition group-hover:text-wi-gold">
								WorthyIdea
							</span>
							<span className="text-[10px] text-wi-text/45 tracking-[0.12em]">Build Consensus Faster</span>
						</NavLink>
						{showBadge ? <WorthyHumanBadge /> : null}
					</div>
						<nav className="flex flex-wrap items-center gap-0.5">
							<NavLink to="/" className={navClass} end>
								Home
							</NavLink>
							<NavLink to="/about" className={navClass}>
								About
							</NavLink>
							<NavLink to="/membership" className={navClass}>
								Membership
							</NavLink>
								<div className="ml-1 flex items-center gap-1">
									<FontScaleControl />
									<ThemeControl />
								</div>
							</nav>
				</div>
			</header>
			<motion.main
				id="main-content"
				tabIndex={-1}
				key={location.pathname}
				className="mx-auto w-full max-w-5xl flex-1 px-4 py-10 outline-none md:px-6 2xl:px-24"
				initial={{ opacity: prefersReducedMotion ? 1 : 0 }}
				animate={{ opacity: 1 }}
				transition={{
					duration: prefersReducedMotion ? 0 : 0.18,
					ease: [0.2, 0.8, 0.2, 1],
				}}
				style={{ pointerEvents: "auto" }}
			>
				<Outlet />
			</motion.main>
			<footer className="border-wi-text/[0.07] border-t bg-wi-bg/30 py-8 text-center text-wi-text/48 text-xs">
				<div className="mx-auto max-w-5xl space-y-2.5 px-4 leading-relaxed md:px-6 2xl:px-24">
					<p>
						<span className="block text-wi-text/40 sm:inline sm:after:content-['·'] sm:after:px-2">
							Clear decisions for groups of any size. Ad free private and secure.
						</span>
						<NavLink
							to="/about"
							className="font-medium text-wi-text/55 underline-offset-2 transition-colors duration-150 ease-in-out hover:text-wi-gold hover:underline"
						>
							About
						</NavLink>
						{" · "}
						<NavLink
							to="/membership"
							className="font-medium text-wi-text/55 underline-offset-2 transition-colors duration-150 ease-in-out hover:text-wi-gold hover:underline"
						>
							Membership
						</NavLink>
						{" · "}
						<a
							href={CUSTOMER_CONTACT_MAILTO}
							className="font-medium text-wi-text/55 underline-offset-2 transition-colors duration-150 ease-in-out hover:text-wi-gold hover:underline"
						>
							Contact
						</a>
					</p>
					<p className="text-[11px] text-wi-text/38">
						<NavLink
							to="/help"
							className="font-medium text-wi-text/48 underline-offset-2 transition-colors duration-150 ease-in-out hover:text-wi-gold/90 hover:underline"
						>
							Help
						</NavLink>
						{" · "}
						<NavLink
							to="/help/running-a-session"
							className="font-medium text-wi-text/48 underline-offset-2 transition-colors duration-150 ease-in-out hover:text-wi-gold/90 hover:underline"
						>
							Running a session
						</NavLink>
						{" · "}
						<NavLink
							to="/help/install"
							className="font-medium text-wi-text/48 underline-offset-2 transition-colors duration-150 ease-in-out hover:text-wi-gold/90 hover:underline"
						>
							Install
						</NavLink>
						{" · "}
						<NavLink
							to="/trust"
							className="font-medium text-wi-text/48 underline-offset-2 transition-colors duration-150 ease-in-out hover:text-wi-gold/90 hover:underline"
						>
							Trust
						</NavLink>
						{" · "}
						<NavLink
							to="/privacy"
							className="font-medium text-wi-text/48 underline-offset-2 transition-colors duration-150 ease-in-out hover:text-wi-gold/90 hover:underline"
						>
							Privacy
						</NavLink>
						{" · "}
						<NavLink
							to="/security"
							className="font-medium text-wi-text/48 underline-offset-2 transition-colors duration-150 ease-in-out hover:text-wi-gold/90 hover:underline"
						>
							Security
						</NavLink>
						{" · "}
						<NavLink
							to="/math"
							className="font-medium text-wi-text/48 underline-offset-2 transition-colors duration-150 ease-in-out hover:text-wi-gold/90 hover:underline"
						>
							Math
						</NavLink>
						{" · "}
						<a
							href={CUSTOMER_CONTACT_MAILTO}
							className="font-medium text-wi-text/48 underline-offset-2 transition-colors duration-150 ease-in-out hover:text-wi-gold/90 hover:underline"
						>
							{CUSTOMER_CONTACT_EMAIL}
						</a>
					</p>
				</div>
			</footer>
		</div>
	);
}
